import React, { useEffect, useMemo, useRef, useState } from 'react';
import ekoImage from '../assets/eko-_transparent-removebg-preview.png';
import AnswerReview from './AnswerReview';
import FeedbackForm from './FeedbackForm';
import SendLetterDialog from './SendLetterDialog';
import { captureElementPdf } from '../services/pagePdf';
import { testTitle, withCurrentTestNames } from '../data/tests';
import './SnapshotLetter.css';

/* ------------------------------------------------------------------ *
 * Types — the shape the letter UI renders.
 * `normalizeSnapshotLetter` maps the API payload onto this shape, so
 * this is the only contract the presentation layer depends on.
 * ------------------------------------------------------------------ */

/** A "wrote X for Y" pair — the concrete proof behind an observation. */
export interface LetterEvidence {
  wrote: string;
  target: string;
  note?: string;
}

/** A number worth showing on its own, e.g. 171 words a minute. */
export interface LetterStat {
  value: string;
  label?: string;
}

export interface LetterSuggestion {
  title: string;
  body: string[];
  because?: string;
}

export interface LetterObservation {
  title: string;
  domain?: string;
  body: string[];
  seenIn?: string;
  signals?: string[];
  evidence?: LetterEvidence[];
  stats?: LetterStat[];
}

export interface LetterBlock {
  title?: string;
  body: string[];
  signals?: string[];
}

/** A growth edge from `still_growing[]`, shown with the suggestion it carries. */
export interface LetterGrowth {
  title: string;
  body: string[];
  seenIn?: string;
  suggestion?: LetterSuggestion;
}

/** A line a parent could raise at a parent-teacher conference. */
export interface LetterConferenceItem {
  claim: string;
  ask?: string;
}

export interface LetterArea {
  title: string;
  body: string[];
  seenIn?: string;
  frequency?: 'repeated' | 'once';
}

export interface SnapshotLetterData {
  childName: string;
  salutation: string;
  headline: { lead: string; accent?: string };
  opening: string[];
  noticed: LetterObservation[];
  helped?: LetterBlock;
  growing: LetterGrowth[];
  /** The "this level was out of reach" note, present only when the run suggests one. */
  levelNote?: { title: string; body: string[] };
  suggestions: LetterSuggestion[];
  conference?: { title?: string; subtitle?: string; items: LetterConferenceItem[] };
  closing?: { line?: string; signature?: string };
  enclosed?: { title: string; subtitle?: string; areas: LetterArea[] };
  disclaimer: string[];
  branding: string;
  meta?: { grade?: string; activities?: string[] };
}

/* ------------------------------------------------------------------ *
 * Normalizer
 * ------------------------------------------------------------------ */

/** Trim, and call every activity by the name the child saw on the journey. */
const clean = (value: any): string => withCurrentTestNames(String(value ?? '').trim());

const toParagraphs = (value: any): string[] => {
  if (Array.isArray(value)) return value.map(clean).filter(Boolean);
  if (typeof value === 'string') {
    return withCurrentTestNames(value)
      .split(/\n\s*\n|\n/)
      .map((p) => p.trim())
      .filter(Boolean);
  }
  return [];
};

const toList = (value: any): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((v) => (typeof v === 'string' ? v : v?.tag ?? v?.label ?? v?.name ?? v?.signal ?? ''))
    .map(clean)
    .filter(Boolean);
};

/** `seen_in` names activities, so each entry resolves to its journey name. */
const toSeenIn = (value: any): string | undefined => {
  if (typeof value === 'string' && value.trim()) return clean(value);
  const list = toList(value).map(testTitle);
  return list.length ? list.join(' · ') : undefined;
};

/** Breaks a single string into sentences, so a long line can be set over several. */
const toSentences = (value: any): string[] => {
  const text = String(value ?? '').trim();
  if (!text) return [];
  return (text.match(/[^.!?]+[.!?]*/g) || [text]).map(clean).filter(Boolean);
};

/* The design sets the tail of the headline in italic accent type. The API sends one
   sentence, so split it at the connective nearest the middle for a natural break. */
const CONNECTIVES = new Set([
  'until', 'when', 'because', 'while', 'even', 'and', 'but', 'so', 'as', 'to', 'with',
  'before', 'after', 'once', 'though', 'that', 'where', 'if', 'for', 'in', 'on', 'by',
]);

const splitHeadline = (raw: any): { lead: string; accent?: string } => {
  if (raw && typeof raw === 'object' && (raw.lead || raw.accent)) {
    return { lead: String(raw.lead || '').trim(), accent: raw.accent ? String(raw.accent).trim() : undefined };
  }
  const text = clean(raw);
  if (!text) return { lead: '' };
  const words = text.split(/\s+/);
  if (words.length < 8) return { lead: text };

  const target = words.length * 0.55;
  let cut = -1;
  words.forEach((word, i) => {
    if (i < 3 || i > words.length - 3) return;
    if (!CONNECTIVES.has(word.toLowerCase().replace(/[^a-z]/g, ''))) return;
    if (cut === -1 || Math.abs(i - target) < Math.abs(cut - target)) cut = i;
  });
  if (cut === -1) cut = Math.round(target);

  return { lead: words.slice(0, cut).join(' '), accent: words.slice(cut).join(' ') };
};

const toEvidence = (value: any): LetterEvidence[] =>
  (Array.isArray(value) ? value : [])
    .map((e: any) => ({
      wrote: String(e?.wrote ?? e?.written ?? e?.child ?? e?.user_input ?? e?.said ?? '').trim(),
      target: String(e?.target ?? e?.for_word ?? e?.word ?? e?.expected ?? e?.correct ?? '').trim(),
      note: e?.note || e?.detail ? String(e.note || e.detail).trim() : undefined,
    }))
    .filter((e: LetterEvidence) => e.wrote && e.target);

const toStats = (value: any): LetterStat[] =>
  (Array.isArray(value) ? value : [])
    .map((s: any) =>
      typeof s === 'string'
        ? { value: s.trim() }
        : {
            value: String(s?.value ?? s?.number ?? s?.figure ?? '').trim(),
            label: s?.label || s?.name ? String(s.label || s.name).trim() : undefined,
          }
    )
    .filter((s: LetterStat) => !!s.value);

const toSuggestion = (raw: any): LetterSuggestion | undefined => {
  if (!raw) return undefined;
  const suggestion: LetterSuggestion = {
    title: clean(raw?.title || raw?.action),
    body: toParagraphs(raw?.body ?? raw?.text ?? raw?.detail),
    because:
      raw?.because || raw?.rationale || raw?.why ? clean(raw.because || raw.rationale || raw.why) : undefined,
  };
  return suggestion.title || suggestion.body.length ? suggestion : undefined;
};

const normalizeFrequency = (value: any): 'repeated' | 'once' => {
  const text = String(value ?? '').toLowerCase();
  if (!text) return 'repeated';
  if (text.includes('once') || text.includes('light') || text === 'single') return 'once';
  return 'repeated';
};

/** Internal test keys from `tests_completed` mapped to the names parents see. */
const toActivityNames = (value: any): string[] => toList(value).map(testTitle);

/**
 * Maps the `POST /snapshot/` response onto `SnapshotLetterData`.
 *
 * The payload nests the letter under `snapshot`, with `child_name`/`grade`/
 * `tests_completed` at the top level. Key aliases are kept so a pre-normalized
 * object (or the local sample) can be passed straight in.
 */
export const normalizeSnapshotLetter = (raw: any, fallbackName?: string): SnapshotLetterData | null => {
  if (!raw || typeof raw !== 'object') return null;
  const src = raw.snapshot ?? raw.letter ?? raw.data ?? raw;
  if (!src || typeof src !== 'object') return null;

  const childName = String(raw.child_name || src.child_name || src.childName || fallbackName || 'your child').trim();
  const firstName = childName.split(' ')[0];

  // `opening` is `{ headline, paragraph }` on the wire; older shapes send plain text.
  const openingSrc = src.opening;
  const headlineSrc = openingSrc?.headline ?? src.headline ?? src.title;
  const opening = openingSrc?.paragraph
    ? toParagraphs(openingSrc.paragraph)
    : toParagraphs(openingSrc ?? src.intro);

  const noticedSrc = src.what_i_noticed ?? src.noticed ?? src.observations ?? [];
  const growingSrc = src.still_growing ?? src.growing ?? [];
  const helpedSrc = src.what_helped ?? src.helped;
  const areasSrc = src.full_picture ?? src.enclosed?.areas ?? src.learning_areas ?? [];

  const growingList: any[] = Array.isArray(growingSrc) ? growingSrc : growingSrc ? [growingSrc] : [];

  const helped: LetterBlock | undefined = (() => {
    if (!helpedSrc) return undefined;
    const body = toParagraphs(helpedSrc.paragraph ?? helpedSrc.headline ?? helpedSrc.body ?? helpedSrc);
    const signals = toList(helpedSrc.signals ?? helpedSrc.tags);
    if (!body.length && !signals.length) return undefined;
    return { title: helpedSrc.paragraph ? clean(helpedSrc.headline) : undefined, body, signals };
  })();

  // A growth edge carries its own suggestion, and it reads best right there, so it
  // stays nested. Only loose `things_to_try` entries get their own section.
  const suggestions: LetterSuggestion[] = (Array.isArray(src.things_to_try) ? src.things_to_try : [])
    .map(toSuggestion)
    .filter(Boolean) as LetterSuggestion[];

  const conferenceSrc =
    src.for_the_conference ??
    src.conference ??
    src.parent_teacher_conference ??
    src.parent_teacher ??
    src.talking_points;
  const conferenceItems: LetterConferenceItem[] = (
    Array.isArray(conferenceSrc) ? conferenceSrc : conferenceSrc?.items ?? conferenceSrc?.points ?? []
  )
    .map((c: any) => ({
      claim: clean(c?.claim || c?.headline || c?.point || c?.title || c),
      ask: c?.ask || c?.worth_asking || c?.note ? clean(c.ask || c.worth_asking || c.note) : undefined,
    }))
    .filter((c: LetterConferenceItem) => !!c.claim);

  // `level_note` arrives only when the run says the set did not fit; nothing to show otherwise.
  const levelSrc = src.level_note;
  const levelNote =
    levelSrc && (levelSrc.headline || levelSrc.title || levelSrc.paragraph || levelSrc.body)
      ? {
          title: clean(levelSrc.headline || levelSrc.title),
          body: toParagraphs(levelSrc.paragraph ?? levelSrc.body),
        }
      : undefined;

  return {
    childName,
    salutation: String(src.salutation || 'Dear Parent,').trim(),
    headline: splitHeadline(headlineSrc),
    opening,
    noticed: (Array.isArray(noticedSrc) ? noticedSrc : [])
      .map((n: any) => ({
        title: clean(n.headline || n.title),
        domain: n.area_display_name || n.domain || n.area || undefined,
        body: toParagraphs(n.paragraph ?? n.body ?? n.text),
        seenIn: toSeenIn(n.seen_in ?? n.seenIn),
        signals: toList(n.learning_signals ?? n.signals ?? n.tags),
        evidence: toEvidence(n.evidence ?? n.examples ?? n.quotes),
        stats: toStats(n.stats ?? n.numbers ?? n.measures),
      }))
      .filter((n: LetterObservation) => n.title || n.body.length),
    helped,
    growing: growingList
      .map((g: any) => ({
        title: clean(g?.headline || g?.title),
        body: toParagraphs(g?.paragraph ?? g?.body ?? g?.text),
        seenIn: toSeenIn(g?.seen_in ?? g?.seenIn),
        suggestion: toSuggestion(g?.suggestion),
      }))
      .filter((g: LetterGrowth) => g.title || g.body.length),
    levelNote,
    suggestions,
    conference: conferenceItems.length
      ? {
          title:
            conferenceSrc?.headline || conferenceSrc?.title
              ? clean(conferenceSrc.headline || conferenceSrc.title)
              : undefined,
          subtitle: conferenceSrc?.subtitle ? clean(conferenceSrc.subtitle) : undefined,
          items: conferenceItems,
        }
      : undefined,
    closing: {
      line:
        typeof src.closing === 'string'
          ? clean(src.closing)
          : clean(src.closing?.line || src.closing_line) || undefined,
      signature: src.closing?.signature || src.signature || 'Eko',
    },
    enclosed: {
      title: String(src.enclosed?.title || 'Enclosed: the full picture').trim(),
      subtitle: src.enclosed?.subtitle || 'Every learning area, one at a time.',
      areas: (Array.isArray(areasSrc) ? areasSrc : [])
        .map((a: any) => ({
          title: clean(a.area_display_name || a.title || a.domain),
          body: toParagraphs(a.paragraph ?? a.body ?? a.summary),
          seenIn: toSeenIn(a.seen_in ?? a.seenIn),
          frequency: normalizeFrequency(a.badge ?? a.frequency ?? a.confidence),
        }))
        .filter((a: LetterArea) => a.title || a.body.length),
    },
    disclaimer: src.disclaimer
      ? toSentences(src.disclaimer)
      : [
          `This is a starting point for a conversation about how ${firstName} learns.`,
          'It is not a diagnosis or a formal assessment, and it compares them to no one.',
        ],
    branding: String(src.branding || 'The Dear Parent Project').trim(),
    meta: {
      grade: raw.grade || src.grade || undefined,
      activities: toActivityNames(raw.tests_completed ?? src.meta?.tests_completed),
    },
  };
};

/* ------------------------------------------------------------------ *
 * Small presentational pieces
 * ------------------------------------------------------------------ */

/** Card tints, cycled so a run of cards alternates warm and cool. */
const TONES = ['rose', 'mint', 'sky', 'amber'] as const;

/** A friendly face for each learning area the API can send. */
const DOMAIN_ICONS: Array<[RegExp, string]> = [
  [/listen|hearing|sound/i, '👂'],
  [/language|express|vocab|speech|voice/i, '🗣️'],
  [/pattern|structure|spell|phonic/i, '🧩'],
  [/reason|meaning|think|logic|problem/i, '🔗'],
  [/pace|approach|persist|effort|stamina/i, '🚀'],
  [/attention|focus|memory/i, '🎯'],
  [/confiden|feel|emotion|mood/i, '💛'],
  [/read|story|comprehen|writing/i, '📖'],
];

const domainIcon = (label?: string) => DOMAIN_ICONS.find(([re]) => re.test(label || ''))?.[1] ?? '✨';

const TRY_ICONS = ['🌱', '🧩', '📚', '🎲', '🔭', '🖍️'];

const COUNT_WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight'];
const countWord = (n: number) => COUNT_WORDS[n] ?? String(n);

/** "Logic Quest, Story Explorer and Word Wizard" */
const joinNames = (items: string[]) =>
  items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

const Chips: React.FC<{ items?: string[]; tone?: string }> = ({ items, tone }) => {
  if (!items || !items.length) return null;
  return (
    <div className="sl-chips">
      {items.map((item, i) => (
        <span key={`${item}-${i}`} className="sl-chip" data-tone={tone}>
          {item}
        </span>
      ))}
    </div>
  );
};

const SeenIn: React.FC<{ value?: string }> = ({ value }) =>
  value ? (
    <div className="sl-seen">
      <span className="sl-seen__label">Seen in</span>
      {value}
    </div>
  ) : null;

/** "ov for of" — what the child wrote, next to the word it was aiming at. */
const Evidence: React.FC<{ items?: LetterEvidence[] }> = ({ items }) => {
  if (!items || !items.length) return null;
  return (
    <div className="sl-ev">
      {items.map((e, i) => (
        <div className="sl-ev__pair" key={i}>
          <span className="sl-ev__wrote">{e.wrote}</span>
          <span className="sl-ev__for">for</span>
          <span className="sl-ev__target">{e.target}</span>
          {e.note && <span className="sl-ev__note">{e.note}</span>}
        </div>
      ))}
    </div>
  );
};

const Stats: React.FC<{ items?: LetterStat[] }> = ({ items }) => {
  if (!items || !items.length) return null;
  return (
    <div className="sl-stats">
      {items.map((s, i) => (
        <div className="sl-stat" key={i}>
          <span className="sl-stat__value">{s.value}</span>
          {s.label && <span className="sl-stat__label">{s.label}</span>}
        </div>
      ))}
    </div>
  );
};

const Reveal: React.FC<{ children: React.ReactNode; className?: string; id?: string; style?: React.CSSProperties }> = ({
  children,
  className,
  id,
  style,
}) => (
  <div id={id} className={`sl-reveal${className ? ` ${className}` : ''}`} style={style} data-reveal>
    {children}
  </div>
);

/** The hand-drawn underline beneath the hero headline. */
const Swoosh: React.FC = () => (
  <svg className="sl-swoosh" viewBox="0 0 232 12" fill="none" aria-hidden="true">
    <path
      d="M4 8.6C41 3.4 96 1.8 142 4.2c31 1.6 58 4 86 3.2"
      stroke="currentColor"
      strokeWidth="4"
      strokeLinecap="round"
    />
  </svg>
);

/* ------------------------------------------------------------------ *
 * The snapshot
 * ------------------------------------------------------------------ */

interface SnapshotLetterProps {
  letter: SnapshotLetterData;
  onBack?: () => void;
  /** Both are needed to fetch the per-activity answer review. */
  childId?: string;
  grade?: string;
  /** Just the letter shell - no toolbar, rail or dialogs. The admin preview uses this. */
  bare?: boolean;
  /** Answer payloads already fetched (admin preview), skipping the calls. */
  prefetchedAnswers?: Record<string, any>;
  /** Lets a parent component trigger the page-as-PDF capture. */
  captureRef?: React.MutableRefObject<(() => Promise<Blob>) | null>;
}

/** Side-rail entries, limited to the sections this particular letter actually has. */
const railSections = (letter: SnapshotLetterData) =>
  [
    { id: 'sl-letter', label: 'Snapshot', show: true },
    { id: 'sl-noticed', label: 'Mind', show: letter.noticed.length > 0 },
    { id: 'sl-helped', label: 'Helped', show: !!letter.helped },
    { id: 'sl-growing', label: 'Growing', show: letter.growing.length > 0 },
    { id: 'sl-level', label: 'Level', show: !!letter.levelNote },
    { id: 'sl-try', label: 'To try', show: letter.suggestions.length > 0 },
    { id: 'sl-conference', label: 'Conference', show: !!letter.conference?.items.length },
    { id: 'sl-enclosed', label: 'Full picture', show: !!letter.enclosed?.areas.length },
  ].filter((s) => s.show);

const SnapshotLetter: React.FC<SnapshotLetterProps> = ({ letter, onBack, childId, grade, bare, prefetchedAnswers, captureRef }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useState('sl-letter');
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const canReview = !!(childId && grade);
  const sections = useMemo(() => {
    const base = railSections(letter);
    return canReview ? [...base, { id: 'sl-review', label: 'Answers', show: true }] : base;
  }, [letter, canReview]);

  // Reveal blocks as they scroll into view.
  useEffect(() => {
    const nodes = rootRef.current?.querySelectorAll<HTMLElement>('[data-reveal]');
    if (!nodes?.length) return;
    if (!('IntersectionObserver' in window)) {
      nodes.forEach((node) => node.setAttribute('data-shown', 'true'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.setAttribute('data-shown', 'true');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.06 }
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [letter]);

  // Track which section the reader is in, for the side rail.
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return;
    const targets = sections.map((s) => document.getElementById(s.id)).filter(Boolean) as HTMLElement[];
    if (!targets.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveSection(visible[0].target.id);
      },
      { rootMargin: '-20% 0px -60% 0px' }
    );
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, [sections]);

  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // The page itself, as the PDF. Capturing forces every scroll-reveal shown
  // and every answer table open, because the canvas paints exactly what is
  // in the DOM at that moment.
  const capturePagePdf = async (): Promise<Blob> => {
    const element = shellRef.current;
    if (!element) throw new Error('The page is not ready to capture yet.');
    setCapturing(true);
    try {
      // Two frames: let React paint the expanded tables and shown reveals
      // before the canvas reads them.
      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve))
      );
      return await captureElementPdf(element);
    } finally {
      setCapturing(false);
    }
  };

  // The admin preview captures through this ref rather than the send dialog.
  useEffect(() => {
    if (!captureRef) return;
    captureRef.current = capturePagePdf;
    return () => {
      captureRef.current = null;
    };
  });

  const { helped, growing, enclosed, conference } = letter;
  const firstName = letter.childName.split(' ')[0];
  const activities = letter.meta?.activities ?? [];
  const subtitle =
    [letter.meta?.grade && `${letter.meta.grade} grade`, activities.length && `${activities.length} adventures`]
      .filter(Boolean)
      .join(' · ') || 'Learning snapshot';
  // Observations carrying evidence, numbers or long prose need the full width to read.
  const wideNoticed = letter.noticed.some(
    (n) => n.evidence?.length || n.stats?.length || n.body.join(' ').length > 360
  );

  return (
    <div className="sl-page" ref={rootRef} data-capturing={capturing || undefined}>
      {!bare && (
        <div className="sl-bar">
          {onBack && (
            <button type="button" className="sl-btn" onClick={onBack}>
              ← Back
            </button>
          )}
          <div className="sl-bar__title">
            <span>{subtitle}</span>
            {letter.childName}
          </div>
          <div className="sl-bar__spacer" />
          {canReview && (
            <button type="button" className="sl-btn" onClick={() => scrollTo('sl-review')}>
              The answers
            </button>
          )}
          {childId && (
            <button type="button" className="sl-btn" onClick={() => setFeedbackOpen(true)}>
              Share feedback
            </button>
          )}
          <button type="button" className="sl-btn" onClick={() => window.print()}>
            Save as PDF
          </button>
          {canReview && (
            <button
              type="button"
              className="sl-btn sl-btn--primary"
              onClick={() => setMailOpen(true)}
            >
              Email this letter
            </button>
          )}
        </div>
      )}

      {!bare && (
        <SendLetterDialog
          open={mailOpen}
          onClose={() => setMailOpen(false)}
          childId={childId}
          grade={letter.meta?.grade ?? grade}
          childName={letter.childName}
          onCapturePdf={capturePagePdf}
        />
      )}

      {!bare && (
        <FeedbackForm
          isOpen={feedbackOpen}
          onClose={() => setFeedbackOpen(false)}
          userName={letter.childName}
          userGrade={letter.meta?.grade ?? grade}
          childId={childId}
        />
      )}

      {!bare && (
        <nav className="sl-rail" aria-label="Snapshot sections">
          {sections.map((s) => (
            <button key={s.id} type="button" data-active={activeSection === s.id} onClick={() => scrollTo(s.id)}>
              {s.label}
            </button>
          ))}
        </nav>
      )}

      <div className="sl-shell" ref={shellRef}>
        {/* ---------- hero ---------- */}
        <section className="sl-hero" id="sl-letter">
          <Reveal>
            <div className="sl-hero__badge">✳ {firstName}&apos;s learning snapshot</div>
            <div className="sl-hero__eko">
              <img src={ekoImage} alt="Eko" />
            </div>
            <p className="sl-hero__salutation">{letter.salutation}</p>
            <h1 className="sl-hero__title">
              {letter.headline.lead}
              {letter.headline.accent ? <> {letter.headline.accent}</> : null}
            </h1>
            <Swoosh />
            <div className="sl-hero__prose">
              {letter.opening.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            {activities.length > 0 && <p className="sl-hero__source">Drawn from {joinNames(activities)}</p>}
          </Reveal>
        </section>

        {/* ---------- what I noticed ---------- */}
        {letter.noticed.length > 0 && (
          <section className="sl-card" id="sl-noticed">
            <Reveal>
              <h2 className="sl-card__title">How {firstName}&apos;s mind works</h2>
              <p className="sl-card__sub">{countWord(letter.noticed.length)} angles on the same wonderful kid.</p>
            </Reveal>
            <div className="sl-grid" data-wide={wideNoticed}>
              {letter.noticed.map((note, i) => (
                <Reveal key={i} className="sl-tile" style={{ transitionDelay: `${Math.min(i, 5) * 60}ms` }}>
                  <div className="sl-tile__inner" data-tone={TONES[i % TONES.length]}>
                    <div className="sl-tile__head">
                      <span className="sl-tile__icon" aria-hidden="true">
                        {domainIcon(note.domain)}
                      </span>
                      {note.domain && <span className="sl-tile__eyebrow">{note.domain}</span>}
                    </div>
                    <h3 className="sl-tile__title">{note.title}</h3>
                    <div className="sl-tile__body">
                      {note.body.map((p, j) => (
                        <p key={j}>{p}</p>
                      ))}
                    </div>
                    <Evidence items={note.evidence} />
                    <Stats items={note.stats} />
                    <SeenIn value={note.seenIn} />
                    <Chips items={note.signals} tone={TONES[i % TONES.length]} />
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {/* ---------- what helped ---------- */}
        {helped && (
          <section className="sl-card sl-card--band" id="sl-helped">
            <Reveal>
              <div className="sl-band__head">
                <span className="sl-band__icon" aria-hidden="true">
                  🌤️
                </span>
                <p className="sl-card__sub sl-card__sub--band">What helped {firstName} along</p>
              </div>
              {helped.title && <h2 className="sl-band__title">{helped.title}</h2>}
              <div className="sl-band__body">
                {helped.body.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <Chips items={helped.signals} tone="mint" />
            </Reveal>
          </section>
        )}

        {/* ---------- still growing ---------- */}
        {growing.length > 0 && (
          <section className="sl-card" id="sl-growing">
            <Reveal>
              <h2 className="sl-card__title">Where {firstName} is still growing</h2>
              <p className="sl-card__sub">Edges to grow into, not gaps to worry about.</p>
            </Reveal>
            <div className="sl-rows">
              {growing.map((g, i) => (
                <Reveal key={i} className="sl-row" style={{ transitionDelay: `${Math.min(i, 5) * 60}ms` }}>
                  <div className="sl-row__inner" data-tone="amber">
                    <span className="sl-row__icon" aria-hidden="true">
                      🌿
                    </span>
                    <div className="sl-row__text">
                      {g.title && <h3 className="sl-row__title">{g.title}</h3>}
                      {g.body.map((p, j) => (
                        <p key={j}>{p}</p>
                      ))}
                      <SeenIn value={g.seenIn} />
                      {g.suggestion && (
                        <div className="sl-try">
                          <div className="sl-try__label">Try this</div>
                          {g.suggestion.title && <h4 className="sl-try__title">{g.suggestion.title}</h4>}
                          {g.suggestion.body.map((p, j) => (
                            <p key={j}>{p}</p>
                          ))}
                          {g.suggestion.because && <div className="sl-because">{g.suggestion.because}</div>}
                        </div>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {/* ---------- a note on the level ---------- */}
        {letter.levelNote && (letter.levelNote.title || letter.levelNote.body.length > 0) && (
          <section className="sl-card sl-card--band" id="sl-level">
            <Reveal>
              <div className="sl-band__head">
                <span className="sl-band__icon" aria-hidden="true">
                  🧭
                </span>
                <p className="sl-card__sub sl-card__sub--band">A note on the level</p>
              </div>
              {letter.levelNote.title && <h2 className="sl-band__title">{letter.levelNote.title}</h2>}
              <div className="sl-band__body">
                {letter.levelNote.body.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </Reveal>
          </section>
        )}

        {/* ---------- things to try ---------- */}
        {letter.suggestions.length > 0 && (
          <section className="sl-card" id="sl-try">
            <Reveal>
              <h2 className="sl-card__title">A few things to try together</h2>
              <p className="sl-card__sub">Small, doable ideas, only if the moment feels right.</p>
            </Reveal>
            <div className="sl-rows">
              {letter.suggestions.map((s, i) => (
                <Reveal key={i} className="sl-row" style={{ transitionDelay: `${Math.min(i, 5) * 60}ms` }}>
                  <div className="sl-row__inner" data-tone="mint">
                    <span className="sl-row__icon" aria-hidden="true">
                      {TRY_ICONS[i % TRY_ICONS.length]}
                    </span>
                    <div className="sl-row__text">
                      <h3 className="sl-row__title">{s.title}</h3>
                      {s.body.map((p, j) => (
                        <p key={j}>{p}</p>
                      ))}
                      {s.because && <div className="sl-because">{s.because}</div>}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {/* ---------- for a parent-teacher conference ---------- */}
        {conference && conference.items.length > 0 && (
          <section className="sl-card" id="sl-conference">
            <Reveal>
              <h2 className="sl-card__title">
                {conference.title ||
                  `${countWord(conference.items.length)} things you could mention at a parent-teacher conference`}
              </h2>
              <p className="sl-card__sub">{conference.subtitle || 'Ready to read out, if it helps.'}</p>
            </Reveal>
            <div className="sl-points">
              {conference.items.map((item, i) => (
                <Reveal key={i} className="sl-point" style={{ transitionDelay: `${Math.min(i, 5) * 60}ms` }}>
                  <div className="sl-point__inner">
                    <span className="sl-point__num" aria-hidden="true">
                      {i + 1}
                    </span>
                    <div className="sl-point__text">
                      <p className="sl-point__claim">{item.claim}</p>
                      {item.ask && <p className="sl-point__ask">{item.ask}</p>}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {/* ---------- Eko's closing note ---------- */}
        {(letter.closing?.line || letter.closing?.signature) && (
          <section className="sl-card sl-card--note">
            <Reveal>
              {letter.closing.line && <p className="sl-note__line">{letter.closing.line}</p>}
              {letter.closing.signature && (
                <div className="sl-sign">
                  <span className="sl-sign__avatar">
                    <img src={ekoImage} alt="" />
                  </span>
                  <span className="sl-sign__name">{letter.closing.signature}</span>
                </div>
              )}
            </Reveal>
          </section>
        )}

        {/* ---------- the full picture ---------- */}
        {enclosed && enclosed.areas.length > 0 && (
          <section className="sl-card" id="sl-enclosed">
            <Reveal>
              <h2 className="sl-card__title">{enclosed.title}</h2>
              {enclosed.subtitle && <p className="sl-card__sub">{enclosed.subtitle}</p>}
            </Reveal>
            <div className="sl-rows">
              {enclosed.areas.map((area, i) => (
                <Reveal key={i} className="sl-row">
                  <div className="sl-row__inner sl-row__inner--plain" data-soft={area.frequency === 'once'}>
                    <span className="sl-row__icon" aria-hidden="true">
                      {domainIcon(area.title)}
                    </span>
                    <div className="sl-row__text">
                      <div className="sl-area__head">
                        <h3 className="sl-row__title">{area.title}</h3>
                        <span className="sl-badge" data-soft={area.frequency === 'once'}>
                          {area.frequency === 'once' ? 'Seen once' : 'Seen repeatedly'}
                        </span>
                      </div>
                      {area.body.map((p, j) => (
                        <p key={j}>{p}</p>
                      ))}
                      <SeenIn value={area.seenIn} />
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal>
              <div className="sl-legend">
                <span>
                  <i className="sl-dot" /> Seen more than once
                </span>
                <span>
                  <i className="sl-dot sl-dot--hollow" /> Seen once, so held lightly
                </span>
              </div>
            </Reveal>
          </section>
        )}

        {canReview && (
          <AnswerReview
            childId={childId!}
            grade={grade!}
            childName={firstName}
            expandAll={capturing}
            prefetched={prefetchedAnswers}
          />
        )}

        <div className="sl-disclaimer">
          {letter.disclaimer.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>

        <div className="sl-footer">{letter.branding}</div>
      </div>
    </div>
  );
};

export default SnapshotLetter;
